import { ArrowDown2 } from 'iconsax-react';
import React, { ForwardedRef } from 'react';

import Close from '@/assets/icons/Close';
import IconButton from '@/components/IconButton';

import classNames from '../../../helpers/classNames';
import { BaseSelectButtonProps, SelectButtonProps } from '../types';
import styles from './SelectButton.module.scss';

interface SelectButtonIconProps {
  onReset?: () => void;
  hasSelectedOption: boolean;
  disabled?: boolean;
  variant?: 'white' | 'tertiary' | 'minimal';
}

function SelectButtonIcon({
  onReset,
  hasSelectedOption,
  disabled,
  variant = 'white',
}: SelectButtonIconProps) {
  const iconClassName = classNames([styles.icon], {
    [styles.minimalIcon]: variant === 'minimal',
  });

  const arrowIcon = (
    <ArrowDown2
      variant="Linear"
      color="currentColor"
      size={16}
      className={classNames([iconClassName, styles.toggleIcon])}
    />
  );

  if (!onReset) {
    return arrowIcon;
  }

  return hasSelectedOption ? (
    <IconButton
      Icon={Close}
      variant="white"
      size="small"
      className={iconClassName}
      disabled={disabled}
      onClick={(event) => {
        event.preventDefault();

        onReset();
      }}
    />
  ) : (
    arrowIcon
  );
}

function SelectButtonInner<T>(
  props: SelectButtonProps<T>,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const {
    name,
    onClick,
    isOpen,
    customSelectButton,
    additionalButtonContent,
    buttonText,
    buttonContent,
    selected,
    onReset,
    error,
    disabled,
    variant = 'white',
  } = props;

  const buttonProps = {
    'ref': ref,
    'id': name,
    'type': 'button',
    'onClick': onClick,
    'role': 'combobox',
    'aria-haspopup': 'listbox',
    'aria-controls': `${name}_dropdown`,
    'aria-labelledby': `${name}_label`,
    'aria-expanded': isOpen,
    'aria-activedescendant': selected
      ? `${name}_element_${String(selected)}`
      : '',
    disabled,
  } as BaseSelectButtonProps;

  if (customSelectButton) {
    // eslint-disable-next-line react-hooks/refs
    return customSelectButton(buttonProps);
  }

  return (
    <div className={styles.container}>
      <button
        {...buttonProps}
        className={classNames([styles.selectButton, styles[variant]], {
          [styles.opened]: isOpen,
          [styles.active]: !!selected,
          [styles.error]: !!error,
        })}
        title={buttonText}
      >
        {additionalButtonContent}
        <span className={styles.buttonText}>{buttonContent || buttonText}</span>
      </button>
      <SelectButtonIcon
        onReset={onReset}
        disabled={disabled}
        hasSelectedOption={!!selected}
        variant={variant}
      />
    </div>
  );
}

const SelectButton = React.forwardRef(SelectButtonInner);

export default SelectButton;
